import React, { useEffect, useState, useMemo } from 'react';
import { Table, Space, Input, Select, Tag, Button, message } from 'antd';
import { KeyOutlined } from '@ant-design/icons';
import AppLayout from '../../../components/layout/AppLayout';
import { useTranslation } from 'react-i18next';
import { useProjectApi } from '../../../api/projects/projects';
import { useReferential } from '../../../context/ReferentialContext';
import { CODIFICATION_TYPE } from '../../../constant/codification';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { TProject } from '../../../models/project/project';

const { Search } = Input;
const { Option } = Select;

const getSchoolYear = (iso: string) => {
  const d = new Date(iso);
  const y = d.getFullYear();
  return d.getMonth() >= 8 ? `${y}-${y + 1}` : `${y - 1}-${y}`;
};

interface ProjectsListProps {
  projects: TProject[];
}

const ProjectsList: React.FC<ProjectsListProps> = ({ projects }) => {
  const { t } = useTranslation();
  const { getCodificationsByCodeType } = useReferential();
  const navigate = useNavigate();

  const [searchStudent, setSearchStudent] = useState('');
  const [searchProject, setSearchProject] = useState('');
  const [filterType, setFilterType] = useState<number | null>(null);
  const [filterYear, setFilterYear] = useState<string | null>(null);

  const projectTypeCodifs = useMemo(
    () => getCodificationsByCodeType(CODIFICATION_TYPE.PROJECT_TYPE),
    [getCodificationsByCodeType]
  );

  const filtered = useMemo(
    () =>
      projects.filter(p => {
        if (
          searchStudent &&
          !`${p.lastname} ${p.firstname}`
            .toLowerCase()
            .includes(searchStudent.toLowerCase())
        ) return false;
        if (
          searchProject &&
          !p.name.toLowerCase().includes(searchProject.toLowerCase())
        ) return false;
        if (filterType && p.project_type_id !== filterType) return false;
        if (
          filterYear &&
          p.created_at &&
          getSchoolYear(p.created_at) !== filterYear
        )
          return false;
        return true;
      }),
    [projects, searchStudent, searchProject, filterType, filterYear]
  );

  const columns = useMemo<ColumnsType<TProject>>(
    () => [
      { title: t('project.tableHeaders.student'), render: (_, r) => `${r.lastname} ${r.firstname}` },
      { title: t('project.tableHeaders.project'), dataIndex: 'name' },
      {
        title: t('project.tableHeaders.type'),
        render: (_, r) => {
          const id = r.project_type_id;
          const codif = id
            ? projectTypeCodifs.find(c => c.id === id)
            : undefined;
          return codif ? <Tag>{codif.label}</Tag> : '-';
        },
      },
      {
        title: t('project.tableHeaders.schoolYear'),
        render: (_, r) =>
          r.created_at ? getSchoolYear(r.created_at) : '-',
      },
      {
        title: t('project.tableHeaders.status'),
        render: (_, r) =>
          r.validated ? (
            <Tag color="green">{t('project.status.validated')}</Tag>
          ) : (
            <Tag color="orange">{t('project.status.pending')}</Tag>
          ),
        filters: [
          { text: t('project.status.validated'), value: 'validated' },
          { text: t('project.status.pending'), value: 'ongoing' },
        ],
        onFilter: (value, record) =>
          value === 'validated' ? !!record.validated : !record.validated,
      },
      {
        title: t('project.tableHeaders.actions'),
        fixed: 'right',
        render: (_, r) => (
          <Button
            icon={<KeyOutlined />}
            onClick={() => navigate(`/project-details/${r.id}`)}
          />
        ),
      },
    ],
    [navigate, projectTypeCodifs, t]
  );

  return (
    <>
      <Space style={{ marginBottom: 16 }} wrap>
        <Search
          placeholder={t('project.searchPlaceholder')}
          onSearch={setSearchStudent}
          allowClear
          style={{ width: 200 }}
        />
        <Search
          placeholder={t('project.searchPlaceholder')}
          onSearch={setSearchProject}
          allowClear
          style={{ width: 200 }}
        />
        <Select
          placeholder={t('project.searchPlaceholder')}
          onChange={setFilterType}
          allowClear
          style={{ width: 160 }}
          value={filterType ?? undefined}
        >
          {projects
            .map(p => p.project_type_id)
            .filter((id): id is number => Boolean(id))
            .map(id => ({
              id,
              label:
                projectTypeCodifs.find(c => c.id === id)?.label ?? ''
            }))
            .reduce(
              (acc, cur) =>
                acc.find(x => x.id === cur.id)
                  ? acc
                  : [...acc, cur],
              [] as { id: number; label: string }[]
            )
            .map(pt => (
              <Option key={pt.id} value={pt.id}>
                {pt.label}
              </Option>
            ))}
        </Select>
        <Select
          placeholder={t('project.tableHeaders.schoolYear')}
          onChange={setFilterYear}
          allowClear
          style={{ width: 140 }}
          value={filterYear ?? undefined}
        >
          {Array.from(
            new Set(
              projects
                .map(p => p.created_at)
                .filter((d): d is string => Boolean(d))
                .map(getSchoolYear)
            )
          )
            .sort()
            .map(y => (
              <Option key={y} value={y}>
                {y}
              </Option>
            ))}
        </Select>
      </Space>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={filtered}
        pagination={{ pageSize: 20 }}
        scroll={{ x: 'max-content' }}
      />
    </>
  );
};

const List: React.FC = () => {
  const { t } = useTranslation();
  const { getProjectList } = useProjectApi();
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState<TProject[]>([]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await getProjectList();
        setList(Array.isArray(data) ? data : []);
      } catch (err: any) {
        message.error(err.response?.data ?? err.message);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  return (
    <AppLayout title={t('sidebar.projects', 'Projets')} loading={loading}>
      <ProjectsList projects={list} />
    </AppLayout>
  );
};

export default List;
